'use client';
import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, X } from 'lucide-react';

interface ToastProps {
  message: string;
  type: 'success' | 'error' | 'info';
  duration?: number;
  onClose: () => void;
}

const Toast: React.FC<ToastProps> = ({ message, type, duration = 3000, onClose }) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 300); // Wait for fade out animation
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle data-component-id="src/components/ui/Toast.tsx:27:15" data-component-path="src/components/ui/Toast.tsx" data-component-line="27" data-component-end-line="27" data-component-file="Toast.tsx" data-component-name="CheckCircle" data-component-content="%7B%22elementName%22%3A%22CheckCircle%22%2C%22className%22%3A%22w-5%20h-5%20text-green-500%22%7D" className="w-5 h-5 text-green-500" />;
      case 'error':
        return <XCircle data-component-id="src/components/ui/Toast.tsx:29:15" data-component-path="src/components/ui/Toast.tsx" data-component-line="29" data-component-end-line="29" data-component-file="Toast.tsx" data-component-name="XCircle" data-component-content="%7B%22elementName%22%3A%22XCircle%22%2C%22className%22%3A%22w-5%20h-5%20text-red-500%22%7D" className="w-5 h-5 text-red-500" />;
      default:
        return null;
    }
  };

  const getBgColor = () => {
    switch (type) {
      case 'success':
        return 'bg-green-50 border-green-200';
      case 'error':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-blue-50 border-blue-200';
    }
  };

  return (
    <div data-component-id="src/components/ui/Toast.tsx:47:4" data-component-path="src/components/ui/Toast.tsx" data-component-line="47" data-component-end-line="65" data-component-file="Toast.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
      className={`fixed top-4 right-4 z-50 transform transition-all duration-300 ${
        isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
      }`}
    >
      <div data-component-id="src/components/ui/Toast.tsx:52:6" data-component-path="src/components/ui/Toast.tsx" data-component-line="52" data-component-end-line="64" data-component-file="Toast.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`flex items-center gap-3 p-4 rounded-lg border shadow-lg ${getBgColor()}`}>
        {getIcon()}
        <span data-component-id="src/components/ui/Toast.tsx:54:8" data-component-path="src/components/ui/Toast.tsx" data-component-line="54" data-component-end-line="54" data-component-file="Toast.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20font-medium%20text-gray-700%22%7D" className="text-sm font-medium text-gray-700">{message}</span>
        <button data-component-id="src/components/ui/Toast.tsx:55:8" data-component-path="src/components/ui/Toast.tsx" data-component-line="55" data-component-end-line="63" data-component-file="Toast.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22ml-2%20text-gray-400%20hover%3Atext-gray-600%20transition-colors%22%7D"
          onClick={() => {
            setIsVisible(false);
            setTimeout(onClose, 300);
          }}
          className="ml-2 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X data-component-id="src/components/ui/Toast.tsx:62:10" data-component-path="src/components/ui/Toast.tsx" data-component-line="62" data-component-end-line="62" data-component-file="Toast.tsx" data-component-name="X" data-component-content="%7B%22elementName%22%3A%22X%22%2C%22className%22%3A%22w-4%20h-4%22%7D" className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default Toast;
